import { UserRoundSearch, MapPinOff, UserPlus, Split, Flag, AlertTriangle } from "lucide-react";

// Human-readable labels for every flag value the backend can emit.
// Keep in sync with backend/routers/match_rows.py flag enum values.
export const FLAG_LABELS = {
  name_variation:               "Name variation",
  address_discrepancy:          "Address mismatch",
  new_customer:                 "New customer",
  new_vs_existing_discrepancy:  "New vs existing",
  type_conflict:                "Report says existing",
};

// Longer explanations surfaced as tooltips (and by the drawer banner).
export const FLAG_HINTS = {
  type_conflict:
    "The report's order type says this is existing business (MACD/renewal), " +
    "but no Provider Sales customer was matched. Verify the match before " +
    "creating a new customer — this pattern usually means the match was " +
    "missed or the customer was never entered in Provider Sales.",
};

const FLAG_ICONS = {
  name_variation:               UserRoundSearch,
  address_discrepancy:          MapPinOff,
  new_customer:                 UserPlus,
  new_vs_existing_discrepancy:  Split,
  type_conflict:                AlertTriangle,
};

// Flags that indicate a contradiction the reviewer must resolve — rendered
// in warning colors instead of the neutral chip style.
const WARNING_FLAGS = new Set(["type_conflict"]);

/**
 * FlagChip — a small inline pill for a single flag value.
 *
 * Props:
 *   flag   — string, e.g. "name_variation"
 *   rowId  — optional, woven into data-testid for E2E targeting
 */
export function FlagChip({ flag, rowId }) {
  const Icon = FLAG_ICONS[flag] || Flag;
  const label = FLAG_LABELS[flag] || flag;
  const warn = WARNING_FLAGS.has(flag);

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] ${
        warn
          ? "border-[var(--boss-warning-border)] bg-[var(--boss-warning-soft)] text-[var(--boss-warning-text)]"
          : "border-boss-border bg-boss-elevated text-boss-text-muted"
      }`}
      title={FLAG_HINTS[flag] || undefined}
      data-testid={`flag-chip-${rowId || "x"}-${flag}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

/**
 * FlagChipGroup — renders up to `max` chips, then an overflow "+N" pill.
 *
 * Props:
 *   flags  — string[]
 *   rowId  — optional, passed through to each FlagChip
 *   max    — max chips before collapsing (default 2)
 */
export function FlagChipGroup({ flags = [], rowId, max = 2 }) {
  if (!flags || flags.length === 0) {
    return <span className="text-[12px] text-boss-text-muted">—</span>;
  }

  const visible = flags.slice(0, max);
  const overflow = flags.length - visible.length;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {visible.map((f) => (
        <FlagChip key={f} flag={f} rowId={rowId} />
      ))}
      {overflow > 0 && (
        <span
          title={flags.slice(max).map((f) => FLAG_LABELS[f] || f).join(", ")}
          className="inline-flex items-center rounded-full border border-boss-border bg-boss-elevated px-1.5 py-0.5 text-[11px] text-boss-text-muted"
          data-testid={`flag-chip-overflow-${rowId || "x"}`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
